import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Logo from "@/components/app/Logo";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(params.get("error") ? "Connexion Google interrompue." : "");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Bienvenue ${u.name}`);
      navigate("/dashboard");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/7578806/pexels-photo-7578806.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0FACD7]/80 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 opacity-80">Doctobook</div>
          <div className="font-display text-3xl font-bold">La santé, accessible à tous.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center mb-10" data-testid="login-back-home">
            <Logo h={36} />
          </Link>
          <h1 className="font-display text-3xl font-medium tracking-tight">Bon retour</h1>
          <p className="text-slate-500 mt-2">Connectez-vous à votre espace.</p>

          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full mt-8 h-11 border-slate-300 hover:bg-slate-50"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continuer avec Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> OU <div className="h-px flex-1 bg-slate-200" />
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription data-testid="login-error">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" placeholder="vous@exemple.fr" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" placeholder="••••••••" className="mt-1.5 h-11" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 btn-primary text-white" data-testid="login-submit-btn">
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Pas encore de compte ? <Link to="/register" className="text-[#0FACD7] font-bold hover:text-[#FF7A5C]" data-testid="login-to-register-link">Inscription</Link>
          </p>

          <div className="mt-10 p-4 bg-slate-100 rounded-md text-xs text-slate-600 leading-relaxed">
            <div className="font-semibold mb-1">Comptes démo</div>
            patient@doctobook.fr · dr.dubois@doctobook.fr · pharmacie.hotel-de-ville@doctobook.fr · <span className="font-mono">password123</span>
          </div>
        </div>
      </div>
    </div>
  );
}
